import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import { Chip } from '@/design-system'
import { AlbumSummary } from '../../types'
import { formatRelative, formatMonthYear } from '../../utils/date'
import { CardOwnerBadge } from '../CardOwnerBadge'
import { ReportMenu } from '../ReportMenu'
import { useTopicViewers } from '@/hooks/common/useTopicViewers'
import { getPresenceAvatarTone, isValidAvatarUrl } from '@/utils/helper'

export interface AlbumCardProps {
  album: AlbumSummary
  onClick?: () => void
  index?: number
  /** Shows a report kebab on the cover — Discover-only; unset on "my stuff" grids. */
  onReport?: () => void
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  borderRadius: 'var(--ds-radius-lg)',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  animation: `${fadeUp} 480ms var(--ds-ease) both`,
  transition: [
    `transform var(--ds-duration-normal) var(--ds-ease-out, var(--ds-ease))`,
    `box-shadow var(--ds-duration-normal) var(--ds-ease)`,
  ].join(', '),
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 'var(--ds-shadow-lift)',
  },
  '&:hover .album-card-cover': {
    transform: 'scale(1.04)',
  },
  '&:hover .album-card-edge': {
    opacity: 1,
  },
  '&:hover .card-kebab': {
    opacity: 1,
  },
})

const SpectrumEdge = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 2.5,
  zIndex: 3,
  background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
  opacity: 0,
  transition: `opacity var(--ds-duration-normal) var(--ds-ease)`,
})

const Cov = styled(Box)({
  position: 'relative',
  aspectRatio: '4 / 2.9',
  overflow: 'hidden',
  backgroundColor: 'var(--ds-surface)',
})

const Cover = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: `transform var(--ds-duration-slow) var(--ds-ease)`,
})

const Placeholder = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--ds-ink-subtle)',
  fontSize: 13,
  fontWeight: 500,
})

const Veil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.4))',
  pointerEvents: 'none',
})

const LiveBadge = styled(Box)({
  position: 'absolute',
  top: 10,
  right: 10,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 10px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(255,255,255,0.92)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  color: 'var(--ds-ink)',
  fontWeight: 600,
})

const LiveDot = styled('span')({
  width: 6,
  height: 6,
  borderRadius: '50%',
  flexShrink: 0,
  background: '#01dafe',
  boxShadow: '0 0 8px #01dafe',
})

const Stackline = styled(Box)({
  position: 'absolute',
  bottom: 10,
  left: 12,
  display: 'flex',
})

const StackAvatar = styled('span')({
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: '2px solid #fff',
  marginLeft: -6,
  fontSize: 8.5,
  fontWeight: 800,
  color: '#fff',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:first-of-type': { marginLeft: 0 },
})

const Meta = styled(Box)({
  padding: '14px 16px 15px',
})

const Name = styled(Typography)({
  fontSize: 15,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-ink)',
})

const MetaRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 5,
})

const Signal = styled(Typography)({
  fontSize: 11.5,
  color: 'var(--ds-ink-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

const Stats = styled(Typography)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.08em',
  color: 'var(--ds-ink-subtle)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

export function AlbumCard({ album, onClick, index = 0, onReport }: AlbumCardProps) {
  const navigate = useNavigate()
  const isWalkable = Boolean(album.galleryId)
  const isPrivate = album.privacy === 'private'
  const isUnlisted = album.privacy === 'unlisted'
  const relative = formatRelative(album.updatedAt)
  const monthYear = formatMonthYear(album.updatedAt)
  // Real "who's viewed this recently" — driven by the live topic_view socket
  // event (see useTopicViewers), same source as the album detail page and
  // Lightbox. Only ever populated for the topic owner, which is exactly who's
  // looking at this card on their own /albums grid.
  const recentViewers = useTopicViewers(album.id)

  const onGalleryClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/galleries/${album.galleryId}/studio`)
  }

  return (
    <Root
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
    >
      <SpectrumEdge className='album-card-edge' />
      <Cov>
        {album.coverUrl ? (
          <Cover className='album-card-cover' src={album.coverUrl} alt={album.title} loading='lazy' />
        ) : (
          <Placeholder>No photos yet</Placeholder>
        )}
        <Veil />
        {isWalkable ? (
          <Chip
            tone='live'
            onClick={onGalleryClick}
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              height: 24,
              background: 'rgba(13,13,15,0.65)',
              backdropFilter: 'blur(8px)',
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 8.5,
              letterSpacing: '0.12em',
              color: '#ffcf7d',
              cursor: 'pointer',
            }}
          >
            ◈ WALKABLE
          </Chip>
        ) : isPrivate ? (
          <Chip
            tone='live'
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              height: 24,
              background: 'rgba(13,13,15,0.65)',
              backdropFilter: 'blur(8px)',
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 8.5,
              letterSpacing: '0.12em',
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 10 }} /> PRIVATE
          </Chip>
        ) : isUnlisted ? (
          // Made public via Share but not yet turned into a walkable gallery —
          // used to render no badge at all here, which read as "still private"
          // (see #31 feedback: toggling public didn't visibly show as public).
          <Chip
            tone='live'
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              height: 24,
              background: 'rgba(13,13,15,0.65)',
              backdropFilter: 'blur(8px)',
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 8.5,
              letterSpacing: '0.12em',
            }}
          >
            <VpnKeyOutlinedIcon sx={{ fontSize: 10 }} /> KEY ACCESS
          </Chip>
        ) : (
          <Chip
            tone='live'
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              height: 24,
              background: 'rgba(13,13,15,0.65)',
              backdropFilter: 'blur(8px)',
              fontFamily: 'var(--ds-font-mono)',
              fontSize: 8.5,
              letterSpacing: '0.12em',
              color: '#01dafe',
            }}
          >
            <PublicOutlinedIcon sx={{ fontSize: 10 }} /> PUBLIC
          </Chip>
        )}
        {isWalkable && !album.owner && recentViewers.length > 0 && (
          <>
            <LiveBadge>
              <LiveDot />
              {recentViewers.length} VIEWED RECENTLY
            </LiveBadge>
            <Stackline>
              {recentViewers.slice(0, 3).map((v) => {
                const hasPhoto = isValidAvatarUrl(v.profilePicThumbURL || '')
                const tone = getPresenceAvatarTone(v.sourceUserID)
                return (
                  <StackAvatar
                    key={v.sourceUserID}
                    style={
                      hasPhoto
                        ? { backgroundImage: `url(${v.profilePicThumbURL})` }
                        : { background: tone.bg, color: tone.ink }
                    }
                  >
                    {!hasPhoto && v.sourceUserFullName?.[0]?.toUpperCase()}
                  </StackAvatar>
                )
              })}
            </Stackline>
          </>
        )}
        {album.owner && <CardOwnerBadge owner={album.owner} likes={album.likes} />}
        {onReport && <ReportMenu itemLabel={album.title} onReport={onReport} />}
      </Cov>
      <Meta>
        <Name noWrap>{album.title}</Name>
        <MetaRow>
          <Signal>{relative ? `Updated ${relative}` : '\u00A0'}</Signal>
          <Stats>
            {album.photoCount} {monthYear ? `· ${monthYear}` : ''}
          </Stats>
        </MetaRow>
      </Meta>
    </Root>
  )
}
